// supervisor-candidate-review-rerun.ts
//
// Review follow-up P3 (2026-10-01): what happens right before a review session
// (T-9001/T-9002) whose `done` was for an OLDER candidate is offered again
// (code review P3 H1, supervisor-candidate-review-settlement.ts).
//   - M-A: its failure budget starts over. `counted_failures`/`timeout_count`/
//     `infra_retry_count` survive a `done` route, so failures spread over
//     several candidates added up and the third candidate's first failure
//     went straight to `blocked` (or two timeouts to "ticket quá lớn") — a
//     dead end only `archive` left. `physical_runs` is kept: it names the
//     attempt dirs (`attempts/T-9001-<n>`).
//   - M-B: one re-offer per ticket@HEAD per `run`. Today the record
//     (`candidate_review_finished`) is written for every `done` review, so a
//     second stale re-offer cannot happen; if a later change broke that, this
//     fuse stops paid sessions repeating until maxTicks (the session/attempt
//     caps default to unlimited). Keyed on the run's MissionStore object, so it
//     dies with the run — across runs the durable record is the guard.
import {
  readAttemptStateFile,
  retryStateFor,
  withRetryState,
  writeAttemptStateFile,
} from "./supervisor-attempt-state.js";

const reofferedThisRun = new WeakMap<object, Set<string>>();

/** `false` = this ticket@HEAD was already re-offered in this run — do not dispatch it again. */
export async function prepareStaleReviewRerun(
  input: { store: object; controlRoot: string },
  ticketId: string,
  headSha: string,
): Promise<boolean> {
  const seen = reofferedThisRun.get(input.store) ?? new Set<string>();
  reofferedThisRun.set(input.store, seen);
  const key = `${ticketId}@${headSha}`;
  if (seen.has(key)) return false;
  seen.add(key);

  const state = await readAttemptStateFile(input.controlRoot);
  const prior = retryStateFor(state, ticketId);
  await writeAttemptStateFile(
    input.controlRoot,
    withRetryState(state, ticketId, {
      counted_failures: 0,
      physical_runs: prior.physical_runs,
      timeout_count: 0,
      infra_retry_count: 0,
    }),
  );
  return true;
}
